import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Car,
  Calendar,
  MessageSquare,
  BarChart3,
  Plus,
  Edit,
  Trash2,
  LogOut,
  Loader2,
  Eye,
  EyeOff,
  Upload,
  Image,
  FileText,
  CreditCard,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  Banknote,
  Percent,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { t } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);

  // Login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  // Data state
  const [stats, setStats] = useState(null);
  const [cars, setCars] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [durationDiscounts, setDurationDiscounts] = useState([]);
  const [newDiscount, setNewDiscount] = useState({ min_days: '', max_days: '', discount_percent: '' });

  // Car form state
  const [carDialogOpen, setCarDialogOpen] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [carForm, setCarForm] = useState({
    name: '',
    brand: '',
    year: '2024',
    category: 'economy',
    price_per_day: '',
    season_price_per_day: '',
    discount_percent: '0',
    image_url: '',
    features: '',
    seats: '5',
    transmission: 'automatic',
    fuel_type: 'petrol',
  });
  const [carImageFile, setCarImageFile] = useState(null);
  const [carImagePreview, setCarImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  useEffect(() => {
    // Check if already logged in
    const loggedIn = sessionStorage.getItem('adminLoggedIn');
    if (loggedIn === 'true') {
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const response = await axios.get(`${API}/admin/stats`);
        setStats(response.data);
      } else if (activeTab === 'cars') {
        const response = await axios.get(`${API}/cars?available_only=false`);
        setCars(response.data);
      } else if (activeTab === 'bookings') {
        const response = await axios.get(`${API}/admin/bookings`);
        setBookings(response.data);
      } else if (activeTab === 'messages') {
        const response = await axios.get(`${API}/admin/messages`);
        setMessages(response.data);
      } else if (activeTab === 'discounts') {
        const response = await axios.get(`${API}/admin/duration-discounts`);
        setDurationDiscounts(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { username, password });
      setIsLoggedIn(true);
      sessionStorage.setItem('adminLoggedIn', 'true');
      toast.success('Welcome back!');
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    sessionStorage.removeItem('adminLoggedIn');
  };

  const handleCarImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCarImageFile(file);
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setCarImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadCarImage = async () => {
    if (!carImageFile) return carForm.image_url;
    
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', carImageFile);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Return the image URL
      return `${API}/images/${response.data.file_id}`;
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
      return null;
    } finally {
      setImageUploading(false);
    }
  };

  const handleCarSubmit = async (e) => {
    e.preventDefault();
    
    // Upload image first if new file selected
    let imageUrl = carForm.image_url;
    if (carImageFile) {
      imageUrl = await uploadCarImage();
      if (!imageUrl) return;
    }
    
    if (!imageUrl) {
      toast.error('Please upload a car image');
      return;
    }
    
    try {
      const carData = {
        ...carForm,
        image_url: imageUrl,
        price_per_day: parseFloat(carForm.price_per_day),
        season_price_per_day: carForm.season_price_per_day ? parseFloat(carForm.season_price_per_day) : null,
        discount_percent: parseFloat(carForm.discount_percent) || 0,
        year: parseInt(carForm.year),
        seats: parseInt(carForm.seats),
        features: carForm.features.split(',').map(f => f.trim()).filter(Boolean),
      };

      if (editingCar) {
        await axios.put(`${API}/admin/cars/${editingCar.id}`, carData);
        toast.success('Car updated successfully');
      } else {
        await axios.post(`${API}/admin/cars`, carData);
        toast.success('Car added successfully');
      }

      setCarDialogOpen(false);
      setEditingCar(null);
      setCarImageFile(null);
      setCarImagePreview(null);
      resetCarForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save car');
    }
  };

  const handleEditCar = (car) => {
    setEditingCar(car);
    setCarForm({
      name: car.name,
      brand: car.brand,
      year: (car.year || 2024).toString(),
      category: car.category,
      price_per_day: car.price_per_day.toString(),
      season_price_per_day: car.season_price_per_day ? car.season_price_per_day.toString() : '',
      discount_percent: (car.discount_percent || 0).toString(),
      image_url: car.image_url,
      features: car.features.join(', '),
      seats: car.seats.toString(),
      transmission: car.transmission,
      fuel_type: car.fuel_type,
    });
    setCarImageFile(null);
    setCarImagePreview(car.image_url);
    setCarDialogOpen(true);
  };

  const handleDeleteCar = async (carId) => {
    if (!window.confirm('Are you sure you want to delete this car?')) return;
    try {
      await axios.delete(`${API}/admin/cars/${carId}`);
      toast.success('Car deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete car');
    }
  };

  const handleBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}/status?status=${status}`);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetCarForm = () => {
    setCarForm({
      name: '',
      brand: '',
      year: '2024',
      category: 'economy',
      price_per_day: '',
      season_price_per_day: '',
      discount_percent: '0',
      image_url: '',
      features: '',
      seats: '5',
      transmission: 'automatic',
      fuel_type: 'petrol',
    });
    setCarImageFile(null);
    setCarImagePreview(null);
  };

  const handleAddDurationDiscount = async () => {
    const { min_days, max_days, discount_percent } = newDiscount;
    if (!min_days || !max_days || !discount_percent) {
      toast.error('Please fill all fields');
      return;
    }
    if (parseInt(min_days) > parseInt(max_days)) {
      toast.error('Min days cannot be greater than max days');
      return;
    }
    try {
      await axios.post(`${API}/admin/duration-discounts`, {
        min_days: parseInt(min_days),
        max_days: parseInt(max_days),
        discount_percent: parseFloat(discount_percent),
      });
      toast.success('Discount rule added');
      setNewDiscount({ min_days: '', max_days: '', discount_percent: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add discount rule');
    }
  };

  const handleDeleteDurationDiscount = async (discountId) => {
    try {
      await axios.delete(`${API}/admin/duration-discounts/${discountId}`);
      toast.success('Discount rule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete discount rule');
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="admin-login">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-2xl p-8">
            <div className="text-center mb-8">
              <div className="relative w-20 h-20 mx-auto mb-4">
                <div className="absolute inset-0 bg-gradient-to-br from-gold/30 to-gold/10 rounded-xl rotate-3"></div>
                <div className="absolute inset-0 bg-primary rounded-xl flex items-center justify-center shadow-lg">
                  <Car className="w-10 h-10 text-gold" />
                </div>
              </div>
              <h1 className="font-serif text-2xl font-bold">
                <span className="text-foreground">Univer</span><span className="text-gold">sal</span>
              </h1>
              <p className="text-[10px] text-muted-foreground tracking-[0.3em] uppercase mt-1">Admin Panel</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">{t('username')}</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-2"
                  required
                  data-testid="admin-username"
                />
              </div>
              <div>
                <Label htmlFor="password">{t('password')}</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-primary"
                disabled={loginLoading}
                data-testid="admin-login-btn"
              >
                {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {t('login')}
              </Button>
            </form>

            <p className="text-center text-xs text-muted-foreground mt-6">
              Default: universal.rent / endri.2026
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen pt-20" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-primary text-primary-foreground min-h-[calc(100vh-5rem)] p-6 hidden lg:block">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="relative w-10 h-10">
                <div className="absolute inset-0 bg-gold/20 rounded-lg rotate-3"></div>
                <div className="absolute inset-0 bg-white/10 rounded-lg flex items-center justify-center">
                  <Car className="w-5 h-5 text-gold" />
                </div>
              </div>
              <span className="font-serif text-xl font-bold">
                <span className="text-white">Univer</span><span className="text-gold">sal</span>
              </span>
            </div>
            <p className="text-[10px] text-white/40 tracking-[0.25em] uppercase ml-13">Admin Panel</p>
          </div>

          <nav className="space-y-2">
            {[
              { id: 'dashboard', icon: BarChart3, label: t('dashboard') },
              { id: 'cars', icon: Car, label: t('manageCars') },
              { id: 'bookings', icon: Calendar, label: t('manageBookings') },
              { id: 'discounts', icon: Percent, label: 'Discounts' },
              { id: 'messages', icon: MessageSquare, label: t('messages') },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === item.id ? 'bg-white/10' : 'hover:bg-white/5'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 mt-8 text-white/70"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>{t('logout')}</span>
          </button>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8">
          {/* Mobile Nav */}
          <div className="flex gap-2 mb-6 lg:hidden overflow-x-auto pb-2">
            {[
              { id: 'dashboard', icon: BarChart3 },
              { id: 'cars', icon: Car },
              { id: 'bookings', icon: Calendar },
              { id: 'discounts', icon: Percent },
              { id: 'messages', icon: MessageSquare },
            ].map((item) => (
              <Button
                key={item.id}
                variant={activeTab === item.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab(item.id)}
              >
                <item.icon className="w-4 h-4" />
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          ) : (
            <>
              {/* Dashboard Tab */}
              {activeTab === 'dashboard' && stats && (
                <div>
                  <h1 className="font-serif text-3xl font-bold mb-8">{t('dashboard')}</h1>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                      { label: t('totalCars'), value: stats.total_cars, color: 'bg-accent' },
                      { label: t('availableCars'), value: stats.available_cars, color: 'bg-success' },
                      { label: t('totalBookings'), value: stats.total_bookings, color: 'bg-warning' },
                      { label: t('revenue'), value: `€${stats.total_revenue.toFixed(2)}`, color: 'bg-gold' },
                    ].map((stat, i) => (
                      <div key={i} className="bg-card border border-border rounded-xl p-6">
                        <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center mb-4`}>
                          <span className="text-2xl text-white font-bold">
                            {i === 0 ? '🚗' : i === 1 ? '✓' : i === 2 ? '📅' : '€'}
                          </span>
                        </div>
                        <p className="text-muted-foreground text-sm">{stat.label}</p>
                        <p className="text-2xl font-bold">{stat.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cars Tab */}
              {activeTab === 'cars' && (
                <div>
                  <div className="flex items-center justify-between mb-8">
                    <h1 className="font-serif text-3xl font-bold">{t('manageCars')}</h1>
                    <Dialog open={carDialogOpen} onOpenChange={setCarDialogOpen}>
                      <DialogTrigger asChild>
                        <Button onClick={() => { setEditingCar(null); resetCarForm(); }} data-testid="add-car-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          {t('addCar')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>{editingCar ? t('editCar') : t('addCar')}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCarSubmit} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Name</Label>
                              <Input
                                value={carForm.name}
                                onChange={(e) => setCarForm({ ...carForm, name: e.target.value })}
                                required
                              />
                            </div>
                            <div>
                              <Label>Brand</Label>
                              <Input
                                value={carForm.brand}
                                onChange={(e) => setCarForm({ ...carForm, brand: e.target.value })}
                                required
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <Label>Year</Label>
                              <Input
                                type="number"
                                min="2000"
                                max="2030"
                                value={carForm.year}
                                onChange={(e) => setCarForm({ ...carForm, year: e.target.value })}
                                required
                              />
                            </div>
                            <div>
                              <Label>Category</Label>
                              <Select value={carForm.category} onValueChange={(v) => setCarForm({ ...carForm, category: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="economy">Economy</SelectItem>
                                  <SelectItem value="comfort">Comfort</SelectItem>
                                  <SelectItem value="luxury">Luxury</SelectItem>
                                  <SelectItem value="suv">SUV</SelectItem>
                                  <SelectItem value="motorcycle">Motorcycle</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Price/Day (€)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={carForm.price_per_day}
                                onChange={(e) => setCarForm({ ...carForm, price_per_day: e.target.value })}
                                required
                              />
                            </div>
                            <div>
                              <Label>Season Price/Day (€) <span className="text-xs text-orange-500">(15 May - 15 Sep)</span></Label>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="Leave empty if same"
                                value={carForm.season_price_per_day}
                                onChange={(e) => setCarForm({ ...carForm, season_price_per_day: e.target.value })}
                              />
                            </div>
                            <div>
                              <Label>Discount (%) <span className="text-xs text-red-500">applied per day</span></Label>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                placeholder="0"
                                value={carForm.discount_percent}
                                onChange={(e) => setCarForm({ ...carForm, discount_percent: e.target.value })}
                                data-testid="discount-input"
                              />
                            </div>
                          </div>
                          <div>
                            <Label>Car Image *</Label>
                            <div className="mt-2">
                              {carImagePreview ? (
                                <div className="relative">
                                  <img 
                                    src={carImagePreview} 
                                    alt="Car preview" 
                                    className="w-full h-40 object-cover rounded-lg"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCarImageFile(null);
                                      setCarImagePreview(null);
                                      setCarForm({ ...carForm, image_url: '' });
                                    }}
                                    className="absolute top-2 right-2 bg-destructive text-white p-1 rounded-full"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors">
                                  <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                                  <span className="text-sm text-muted-foreground">Click to upload image</span>
                                  <span className="text-xs text-muted-foreground mt-1">JPG, PNG up to 5MB</span>
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    onChange={handleCarImageChange}
                                  />
                                </label>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <Label>Seats</Label>
                              <Input
                                type="number"
                                value={carForm.seats}
                                onChange={(e) => setCarForm({ ...carForm, seats: e.target.value })}
                              />
                            </div>
                            <div>
                              <Label>Transmission</Label>
                              <Select value={carForm.transmission} onValueChange={(v) => setCarForm({ ...carForm, transmission: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="automatic">Automatic</SelectItem>
                                  <SelectItem value="manual">Manual</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Fuel Type</Label>
                              <Select value={carForm.fuel_type} onValueChange={(v) => setCarForm({ ...carForm, fuel_type: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="petrol">Petrol</SelectItem>
                                  <SelectItem value="diesel">Diesel</SelectItem>
                                  <SelectItem value="electric">Electric</SelectItem>
                                  <SelectItem value="hybrid">Hybrid</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label>Features (comma-separated)</Label>
                            <Textarea
                              value={carForm.features}
                              onChange={(e) => setCarForm({ ...carForm, features: e.target.value })}
                              placeholder="A/C, Bluetooth, GPS..."
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button type="button" variant="outline" onClick={() => setCarDialogOpen(false)}>
                              {t('cancel')}
                            </Button>
                            <Button type="submit" disabled={imageUploading}>
                              {imageUploading ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                t('save')
                              )}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div className="bg-card border border-border rounded-xl overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Car</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Price/Day</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cars.map((car) => (
                          <TableRow key={car.id}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <img src={car.image_url} alt={car.name} className="w-16 h-12 object-cover rounded" />
                                <div>
                                  <p className="font-medium">{car.name}</p>
                                  <p className="text-sm text-muted-foreground">{car.brand} • {car.year || 2024}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="capitalize">{car.category}</TableCell>
                            <TableCell>€{car.price_per_day}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs ${car.available ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}`}>
                                {car.available ? 'Available' : 'Unavailable'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button size="icon" variant="ghost" onClick={() => handleEditCar(car)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => handleDeleteCar(car.id)}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Bookings Tab */}
              {activeTab === 'bookings' && (
                <div>
                  <h1 className="font-serif text-3xl font-bold mb-8">{t('manageBookings')}</h1>
                  
                  {bookings.length === 0 ? (
                    <p className="text-muted-foreground text-center py-10">No bookings yet</p>
                  ) : (
                    <div className="space-y-4">
                      {bookings.map((booking) => (
                        <div key={booking.id} className="bg-card border border-border rounded-xl p-6">
                          {/* Header */}
                          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-sm bg-secondary px-2 py-1 rounded">
                                  #{booking.id?.slice(0, 8).toUpperCase()}
                                </span>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                                  booking.status === 'paid' ? 'bg-success/10 text-success' :
                                  booking.status === 'confirmed' ? 'bg-accent/10 text-accent' :
                                  booking.status === 'pending' ? 'bg-warning/10 text-warning' :
                                  booking.status === 'cancelled' ? 'bg-destructive/10 text-destructive' :
                                  booking.status === 'completed' ? 'bg-success/10 text-success' :
                                  'bg-secondary text-muted-foreground'
                                }`}>
                                  {booking.status === 'paid' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                                  {booking.status === 'pending' && <Clock className="w-3 h-3 inline mr-1" />}
                                  {booking.status === 'cancelled' && <XCircle className="w-3 h-3 inline mr-1" />}
                                  {t(booking.status) || booking.status}
                                </span>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  booking.payment_method === 'card' ? 'bg-accent/10 text-accent' : 'bg-gold/10 text-gold'
                                }`}>
                                  {booking.payment_method === 'card' ? (
                                    <><CreditCard className="w-3 h-3 inline mr-1" /> Card</>
                                  ) : (
                                    <><Banknote className="w-3 h-3 inline mr-1" /> Cash</>
                                  )}
                                </span>
                              </div>
                              <h3 className="text-xl font-semibold">{booking.customer_name}</h3>
                              <p className="text-sm text-muted-foreground">{booking.customer_email} • {booking.customer_phone}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-3xl font-bold text-gold">€{booking.total_price?.toFixed(2)}</p>
                              <p className="text-sm text-muted-foreground">{booking.total_days} days</p>
                              {booking.child_seat && (
                                <span className="text-xs text-accent">+ Child Seat</span>
                              )}
                            </div>
                          </div>

                          {/* Dates */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div className="bg-secondary/50 rounded-lg p-4">
                              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Pickup</p>
                              <p className="font-medium">
                                {new Date(booking.pickup_date).toLocaleDateString('en-GB', { 
                                  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' 
                                })}
                              </p>
                              <p className="text-accent font-semibold">{booking.pickup_time || '09:00'}</p>
                            </div>
                            <div className="bg-secondary/50 rounded-lg p-4">
                              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Return</p>
                              <p className="font-medium">
                                {new Date(booking.return_date).toLocaleDateString('en-GB', { 
                                  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' 
                                })}
                              </p>
                              <p className="text-accent font-semibold">{booking.return_time || '09:00'}</p>
                            </div>
                          </div>

                          {/* Documents */}
                          <div className="mb-6">
                            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              Documents
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* ID Card */}
                              <div className="border border-border rounded-lg p-4">
                                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">ID Card / Passport</p>
                                {booking.id_card_url ? (
                                  <div className="space-y-2">
                                    <div className="relative bg-secondary rounded-lg overflow-hidden h-32">
                                      <img 
                                        src={`${API}/images/${booking.id_card_url}`}
                                        alt="ID Card"
                                        className="w-full h-full object-contain"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    </div>
                                    <div className="flex gap-2">
                                      <a 
                                        href={`${API}/images/${booking.id_card_url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1"
                                      >
                                        <Button size="sm" variant="outline" className="w-full">
                                          <Eye className="w-4 h-4 mr-1" /> View
                                        </Button>
                                      </a>
                                      <a 
                                        href={`${API}/images/${booking.id_card_url}`}
                                        download={`ID_${booking.customer_name.replace(/\s/g, '_')}.jpg`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1"
                                      >
                                        <Button size="sm" variant="outline" className="w-full">
                                          <Download className="w-4 h-4 mr-1" /> Save
                                        </Button>
                                      </a>
                                    </div>
                                    <p className="text-xs text-muted-foreground text-center">
                                      📱 Mobile: Long press image to save
                                    </p>
                                  </div>
                                ) : (
                                  <p className="text-muted-foreground text-sm">Not uploaded</p>
                                )}
                              </div>

                              {/* License */}
                              <div className="border border-border rounded-lg p-4">
                                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Driver's License</p>
                                {booking.license_url ? (
                                  <div className="space-y-2">
                                    <div className="relative bg-secondary rounded-lg overflow-hidden h-32">
                                      <img 
                                        src={`${API}/images/${booking.license_url}`}
                                        alt="License"
                                        className="w-full h-full object-contain"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    </div>
                                    <div className="flex gap-2">
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="flex-1"
                                        onClick={() => window.open(`${API}/images/${booking.license_url}`, '_blank')}
                                      >
                                        <Eye className="w-4 h-4 mr-1" /> View
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="flex-1"
                                        onClick={() => {
                                          const link = document.createElement('a');
                                          link.href = `${API}/images/${booking.license_url}`;
                                          link.download = `License_${booking.customer_name.replace(/\s/g, '_')}.jpg`;
                                          link.click();
                                        }}
                                      >
                                        <Download className="w-4 h-4 mr-1" /> Save
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-muted-foreground text-sm">Not uploaded</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
                            {booking.status === 'pending' && (
                              <Button 
                                onClick={() => handleBookingStatus(booking.id, 'confirmed')}
                                className="bg-accent hover:bg-accent/90"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Confirm Booking
                              </Button>
                            )}
                            {(booking.status === 'pending' || booking.status === 'confirmed') && (
                              <Button 
                                onClick={() => handleBookingStatus(booking.id, 'paid')}
                                variant="outline"
                                className="border-success text-success hover:bg-success/10"
                              >
                                <Banknote className="w-4 h-4 mr-2" />
                                Mark as Paid
                              </Button>
                            )}
                            {booking.status === 'paid' && (
                              <Button 
                                onClick={() => handleBookingStatus(booking.id, 'completed')}
                                className="bg-success hover:bg-success/90"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Complete
                              </Button>
                            )}
                            {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                              <Button 
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to cancel this booking?')) {
                                    handleBookingStatus(booking.id, 'cancelled');
                                  }
                                }}
                                variant="outline"
                                className="border-destructive text-destructive hover:bg-destructive/10"
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Cancel Booking
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Messages Tab */}
              {activeTab === 'messages' && (
                <div>
                  <h1 className="font-serif text-3xl font-bold mb-8">{t('messages')}</h1>
                  <div className="space-y-4">
                    {messages.length === 0 ? (
                      <p className="text-muted-foreground text-center py-10">No messages yet</p>
                    ) : (
                      messages.map((msg) => (
                        <div key={msg.id} className="bg-card border border-border rounded-xl p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="font-semibold">{msg.name}</h3>
                              <p className="text-sm text-muted-foreground">{msg.email}</p>
                              {msg.phone && <p className="text-sm text-muted-foreground">{msg.phone}</p>}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(msg.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm">{msg.message}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Duration Discounts Tab */}
              {activeTab === 'discounts' && (
                <div>
                  <h1 className="font-serif text-3xl font-bold mb-2">Duration Discounts</h1>
                  <p className="text-muted-foreground mb-8">Set discount percentages based on rental duration. Applied globally to all vehicles.</p>

                  {/* Add New Rule */}
                  <div className="bg-card border border-border rounded-xl p-6 mb-8">
                    <h2 className="font-semibold text-lg mb-4">Add Discount Rule</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
                      <div>
                        <Label>Min Days</Label>
                        <Input
                          type="number"
                          min="1"
                          placeholder="e.g. 10"
                          value={newDiscount.min_days}
                          onChange={(e) => setNewDiscount({ ...newDiscount, min_days: e.target.value })}
                          data-testid="discount-min-days"
                        />
                      </div>
                      <div>
                        <Label>Max Days</Label>
                        <Input
                          type="number"
                          min="1"
                          placeholder="e.g. 15"
                          value={newDiscount.max_days}
                          onChange={(e) => setNewDiscount({ ...newDiscount, max_days: e.target.value })}
                          data-testid="discount-max-days"
                        />
                      </div>
                      <div>
                        <Label>Discount %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          placeholder="e.g. 10"
                          value={newDiscount.discount_percent}
                          onChange={(e) => setNewDiscount({ ...newDiscount, discount_percent: e.target.value })}
                          data-testid="discount-percent"
                        />
                      </div>
                      <Button onClick={handleAddDurationDiscount} className="h-10" data-testid="add-discount-btn">
                        <Plus className="w-4 h-4 mr-2" /> Add Rule
                      </Button>
                    </div>
                  </div>

                  {/* Existing Rules */}
                  <div className="bg-card border border-border rounded-xl overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Min Days</TableHead>
                          <TableHead>Max Days</TableHead>
                          <TableHead>Discount</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {durationDiscounts.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-10 text-muted-foreground">
                              No discount rules set. Add one above.
                            </TableCell>
                          </TableRow>
                        ) : (
                          durationDiscounts.map((d) => (
                            <TableRow key={d.id}>
                              <TableCell className="font-medium">{d.min_days} days</TableCell>
                              <TableCell>{d.max_days} days</TableCell>
                              <TableCell>
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                                  -{d.discount_percent}%
                                </span>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteDurationDiscount(d.id)}
                                  data-testid={`delete-discount-${d.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
